import React, { useState, useEffect } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { FaKey, FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaLock, FaCubes, FaEye } from 'react-icons/fa';
import api from '../axiosSetup';
import CustomNavbar from '../CustomNavbar';
import '../Admin/css/admin.css';
import cfg from '../../config';

/* Lizenzübersicht für den Zunftrat — ausschließlich lesend.
   Änderungen an Modulen oder der Sperre bleiben Administratoren vorbehalten. */

const ZUSTAND = {
    valid:    { farbe: '#4ade80', icon: <FaCheckCircle />,        titel: 'Lizenz gültig' },
    expiring: { farbe: '#f0a500', icon: <FaExclamationTriangle />, titel: 'Lizenz läuft bald ab' },
    expired:  { farbe: '#f87171', icon: <FaTimesCircle />,         titel: 'Lizenz abgelaufen' },
    invalid:  { farbe: '#f87171', icon: <FaTimesCircle />,         titel: 'Lizenz ungültig' },
    missing:  { farbe: '#9aa0a6', icon: <FaKey />,                 titel: 'Keine Lizenz hinterlegt' },
};

// Funktion statt Konstante: auf Modulebene wuerde der Rueckfallwert aus
// config.js eingefroren, bevor AppContext die Serverwerte uebernimmt.
const MODUL_TEXTE = () => ({
    Modul_Veranstaltungen: `${cfg.eventTermPlural}, Kalender und Buchungen`,
    Modul_Haes:            `${cfg.costumeTermPlural}: Verwaltung, Anträge, ${cfg.costumeTerm}typen`,
    Modul_Arbeitseinsatz:  'Arbeitseinsätze und Tauschbörse',
    Modul_Berichte:        'Statistiken, Jahresbericht, Personalakten',
    Modul_Tasks:           'Aufgabenverwaltung',
});

const Zeile = ({ label, children }) => (
    <div style={{
        display: 'flex', justifyContent: 'space-between', gap: '1rem',
        padding: '0.6rem 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
        fontSize: '0.9rem',
    }}>
        <span style={{ color: '#8b9096' }}>{label}</span>
        <span style={{ color: '#e3e5e7', textAlign: 'right' }}>{children}</span>
    </div>
);

const ZRLicense = () => {
    const [info, setInfo]       = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    useEffect(() => {
        api.get('/api/license/overview')
            .then(({ data }) => setInfo(data))
            .catch(() => setError('Die Lizenzinformationen konnten nicht geladen werden.'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <><CustomNavbar />
            <div className="admin-page text-center py-5"><Spinner animation="border" variant="warning" /></div>
        </>
    );

    const z = ZUSTAND[info?.status] || ZUSTAND.missing;
    const laeuftAb = info?.status === 'expiring';
    const problem  = ['expired', 'invalid', 'missing'].includes(info?.status);

    return (
        <>
            <CustomNavbar />
            <div className="admin-page">

                <header className="admin-head">
                    <div className="admin-head__icon"><FaKey /></div>
                    <div>
                        <h1 className="admin-head__title">Lizenz</h1>
                        <p className="admin-head__sub">
                            Übersicht über den Lizenzstatus dieser App. Änderungen sind
                            Administratoren vorbehalten.
                        </p>
                    </div>
                </header>

                {error && <Alert variant="danger">{error}</Alert>}

                {info && (
                    <>
                        {/* ── Statuskarte ─────────────────────────────────── */}
                        <div className="admin-card" style={{ borderColor: `${z.farbe}55`, marginBottom: '1rem' }}>
                            <div className="admin-card__body" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                                <div style={{
                                    width: 46, height: 46, flex: '0 0 auto', borderRadius: 13,
                                    display: 'grid', placeItems: 'center', fontSize: '1.25rem',
                                    background: `${z.farbe}1a`, border: `1px solid ${z.farbe}45`, color: z.farbe,
                                }}>
                                    {z.icon}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '1.08rem', fontWeight: 620, color: '#f0f1f2' }}>{z.titel}</div>
                                    {info.message && (
                                        <div style={{ color: '#9aa0a6', fontSize: '0.88rem', marginTop: 3 }}>{info.message}</div>
                                    )}

                                    {laeuftAb && (
                                        <div style={{
                                            marginTop: '0.8rem', padding: '0.7rem 0.9rem', borderRadius: 10,
                                            background: 'rgba(240,165,0,0.1)', border: '1px solid rgba(240,165,0,0.3)',
                                            color: '#ffcb57', fontSize: '0.87rem', lineHeight: 1.55,
                                        }}>
                                            Die Lizenz läuft in <b>{info.daysLeft} Tagen</b> ab ({info.expDate}).
                                            Bitte rechtzeitig um Verlängerung kümmern — nach Ablauf ist keine
                                            Anmeldung mehr möglich.
                                        </div>
                                    )}

                                    {problem && (
                                        <div style={{
                                            marginTop: '0.8rem', padding: '0.7rem 0.9rem', borderRadius: 10,
                                            background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)',
                                            color: '#fca5a5', fontSize: '0.87rem', lineHeight: 1.55,
                                        }}>
                                            Solange dieser Zustand besteht, ist die App für {cfg.memberTermPlural}
                                            nicht nutzbar. Bitte wende dich an den Hersteller.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ── Eckdaten ────────────────────────────────────── */}
                        <div className="admin-card" style={{ marginBottom: '1rem' }}>
                            <div className="admin-card__head">
                                <h2 className="admin-card__title">Eckdaten</h2>
                                <span style={{ fontSize: '0.75rem', color: '#8b9096', display: 'flex', alignItems: 'center', gap: 5 }}>
                                    <FaEye /> nur lesend
                                </span>
                            </div>
                            <div className="admin-card__body" style={{ paddingTop: '0.3rem' }}>
                                <Zeile label="Lizenznehmer">{info.customer || '—'}</Zeile>
                                <Zeile label="Ausgestellt am">{info.issDate || '—'}</Zeile>
                                <Zeile label="Gültig bis">{info.expDate || '—'}</Zeile>
                                <Zeile label="Verbleibend">
                                    {typeof info.daysLeft === 'number'
                                        ? (info.daysLeft < 0
                                            ? <span style={{ color: '#f87171' }}>seit {Math.abs(info.daysLeft)} Tagen abgelaufen</span>
                                            : `${info.daysLeft} Tage`)
                                        : '—'}
                                </Zeile>
                                {info.maxUsers != null && <Zeile label={`${cfg.memberTermPlural} laut Lizenz`}>{info.maxUsers}</Zeile>}
                                <Zeile label="Lizenzprüfung">
                                    {info.enforced
                                        ? 'aktiv'
                                        : <span style={{ color: '#8b9096' }}>inaktiv (Demo-Betrieb)</span>}
                                </Zeile>
                                {info.systemLocked && (
                                    <Zeile label="Zugang">
                                        <span style={{ color: '#f87171', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                            <FaLock /> durch Administrator gesperrt
                                        </span>
                                    </Zeile>
                                )}
                            </div>
                        </div>

                        {/* ── Enthaltene Module ───────────────────────────── */}
                        <div className="admin-card">
                            <div className="admin-card__head">
                                <h2 className="admin-card__title">
                                    <FaCubes className="me-2" style={{ opacity: 0.6 }} />
                                    Enthaltene Module
                                </h2>
                                <span style={{ fontSize: '0.8rem', color: '#8b9096' }}>{info.modules?.length || 0}</span>
                            </div>
                            <div className="admin-card__body">
                                {info.modules?.length ? info.modules.map(m => (
                                    <div key={m.key} style={{
                                        display: 'flex', alignItems: 'center', gap: '0.7rem',
                                        padding: '0.6rem 0.7rem', marginBottom: '0.45rem',
                                        borderRadius: 10, background: 'rgba(255,255,255,0.02)',
                                        border: '1px solid rgba(255,255,255,0.06)',
                                    }}>
                                        <FaCheckCircle style={{ color: '#4ade80', flex: '0 0 auto' }} />
                                        <div>
                                            <div style={{ color: '#e3e5e7', fontSize: '0.92rem', fontWeight: 550 }}>{m.label}</div>
                                            <div style={{ color: '#8b9096', fontSize: '0.79rem' }}>
                                                {MODUL_TEXTE()[m.name] || m.name}
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <p style={{ color: '#8b9096', fontSize: '0.88rem', margin: 0 }}>
                                        Keine Module in der Lizenz hinterlegt.
                                    </p>
                                )}
                            </div>
                        </div>

                        {info.vendorEmail && (
                            <div className="admin-hint">
                                Fragen zur Lizenz? Wende dich an{' '}
                                <a href={`mailto:${info.vendorEmail}`} style={{ color: '#ffcb57' }}>{info.vendorEmail}</a>.
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default ZRLicense;
